/**
 * K-Skin AI Recommendation Engine v2.0
 * Truly personalized K-beauty recommendations
 *
 * Key Features:
 * - Severity-weighted concerns (higher scores = more focus)
 * - Step-purpose matching (each step has a role)
 * - Ingredient conflict detection (no Retinol + Vitamin C in same routine)
 * - Synergy bonuses (CE Ferulic combo, Niacinamide + HA)
 * - Time-appropriate actives (Retinol PM, Vitamin C AM)
 * - Routine validation (4-point quality check)
 */

import type {
  Product,
  RoutineCategory,
  PriceTier,
  RecommendedStep,
  RecommendedRoutine,
  RoutineType,
  SkinType,
  TextureFeel,
} from '@/types/product'
import {
  PERFECTCORP_TO_DB_CONCERN,
  SURVEY_CONDITION_TO_CONCERN,
  SURVEY_INGREDIENT_TO_NAME,
  PREGNANCY_UNSAFE_INGREDIENTS,
  type DBConcernField,
  type PerfectCorpMetric,
} from '@/data/concernIngredientMap'
import {
  hasConflict,
  hasAnyConflict,
  getSynergyBonus,
  isTimeAppropriate,
  countActiveClasses,
  normalizeIngredient,
} from '@/data/ingredientConflicts'
import {
  STEP_PURPOSES,
  shouldIncludeStep,
  getPreferredTextures,
} from '@/data/stepPurposes'
import type { WeatherInsight, TextureRecommendation } from '@/services/weatherService'
import type { FullSurveyData } from '@/types/fullSurvey'
import type { ShortSurveyData } from '@/types/survey'

// ============================================================================
// Price-Based Tier Thresholds
// ============================================================================
// Instead of relying on manual priceTier tags in product data,
// we determine tier dynamically based on actual price

// Minimum score threshold for a product to be considered "qualified"
const MIN_QUALIFIED_SCORE = 3.0

// ============================================================================
// Types
// ============================================================================

export interface AnalysisScores {
  hd_wrinkle?: number
  hd_pore?: number
  hd_texture?: number
  hd_acne?: number
  hd_oiliness?: number
  hd_radiance?: number
  hd_moisture?: number
  hd_redness?: number
  hd_firmness?: number
  hd_age_spot?: number
  hd_dark_circle?: number
  hd_eye_bag?: number
}

export interface UserProfile {
  analysisScores: AnalysisScores
  survey: ShortSurveyData
  fullSurvey: FullSurveyData
  weather: WeatherInsight
  routineType: RoutineType
}

export interface PrioritizedConcern {
  field: DBConcernField
  score: number
  severity: 'mild' | 'moderate' | 'severe'
  weight: number
}

export interface ProductScore {
  product: Product
  totalScore: number
  breakdown: {
    concernMatch: number // Now includes step purpose (unified)
    skinTypeMatch: number
    avoidPenalty: number // Now includes pregnancy safety
    conflictPenalty: number
    // Optional terms for A/B testing (kept in code, weight can be 0)
    ingredientMatch: number
    textureMatch: number
    synergyBonus: number
    timeBonus: number
  }
  matchReasons: string[]
}

export interface RoutineValidation {
  concernsCovered: boolean
  noConflicts: boolean
  activeBalance: boolean
  hasSunscreen: boolean
  issues: string[]
}

// ============================================================================
// Score Weights (tuned for personalization)
// ============================================================================

const WEIGHTS = {
  // ============================================================================
  // Core terms (always active)
  // ============================================================================
  concernMatch: 4.0, // Unified: user concerns + step purpose (was 3.0 + 2.0)
  skinTypeMatch: 2.0, // Skin type compatibility
  avoidPenalty: 10.0, // Unified: user avoid prefs + pregnancy safety (was 5.0 + 10.0)
  conflictPenalty: 8.0, // Conflicts with already-selected products

  // ============================================================================
  // Optional terms for A/B testing (set to 0 to disable)
  // ============================================================================
  ingredientMatch: 1.5, // User's ingredient preferences
  textureMatch: 1.0, // Weather/texture match
  synergyBonus: 1.5, // Ingredient synergies
  timeBonus: 1.0, // Time-appropriate actives
}

// ============================================================================
// Phase 1: Concern Prioritization with Severity Weights
// ============================================================================

/**
 * Convert analysis scores to prioritized concerns with severity weights
 * Lower scores = worse skin = higher weight (1.0 to 1.5x)
 */
export function prioritizeConcerns(analysisScores: AnalysisScores): PrioritizedConcern[] {
  const concerns: PrioritizedConcern[] = []

  for (const [metric, score] of Object.entries(analysisScores)) {
    if (score === undefined) continue

    const dbFields = PERFECTCORP_TO_DB_CONCERN[metric as PerfectCorpMetric]
    if (!dbFields) continue

    for (const field of dbFields) {
      // Avoid duplicates, keep lowest score (worst condition)
      const existing = concerns.find((c) => c.field === field)
      if (existing) {
        if (score < existing.score) {
          existing.score = score
          existing.severity = getSeverity(score)
          existing.weight = getSeverityWeight(score)
        }
        continue
      }

      concerns.push({
        field,
        score,
        severity: getSeverity(score),
        weight: getSeverityWeight(score),
      })
    }
  }

  // Sort by score (lowest first = worst concerns), return top 3
  return concerns.sort((a, b) => a.score - b.score).slice(0, 3)
}

function getSeverity(score: number): 'mild' | 'moderate' | 'severe' {
  if (score >= 70) return 'mild'      // Good skin condition
  if (score >= 40) return 'moderate'  // Moderate condition
  return 'severe'                      // Needs attention
}

function getSeverityWeight(score: number): number {
  // Lower score = worse condition = higher weight
  if (score < 40) return 1.5   // Severe - highest weight
  if (score < 70) return 1.2   // Moderate
  return 1.0                    // Mild - lowest weight
}

/**
 * Add concerns from survey conditions
 */
function addSurveyConcerns(
  baseConcerns: PrioritizedConcern[],
  fullSurvey: FullSurveyData
): PrioritizedConcern[] {
  const allConcerns = [...baseConcerns]
  const existingFields = new Set(baseConcerns.map((c) => c.field))

  for (const condition of fullSurvey.currentConditions) {
    const mapped = SURVEY_CONDITION_TO_CONCERN[condition]
    if (!mapped) continue

    for (const field of mapped) {
      if (existingFields.has(field)) {
        const existing = allConcerns.find((c) => c.field === field)
        if (existing && existing.score < 50) {
          existing.score = 50
          existing.severity = 'moderate'
          existing.weight = 1.2
        }
        continue
      }

      // Survey-only concerns get moderate weight
      allConcerns.push({
        field,
        score: 50,
        severity: 'moderate',
        weight: 1.2,
      })
      existingFields.add(field)
    }
  }

  return allConcerns
}

// ============================================================================
// Phase 2: Scoring Functions
// ============================================================================

/**
 * Calculate unified concern match (0-1)
 * Combines:
 * - User's prioritized concerns (primary, higher weight)
 * - Step's purpose concerns (secondary, adds if step has specific focus)
 *
 * For dynamic steps (serum, sheet-mask), only user concerns are used.
 * For specific steps (cleanser, toner), step focus concerns add ~20% weight.
 */
function calculateConcernMatch(
  product: Product,
  concerns: PrioritizedConcern[],
  stepCategory: RoutineCategory
): number {
  if (concerns.length === 0) return 0.5

  let weightedMatch = 0
  let totalWeight = 0

  // Part 1: User's concerns (primary - 80% of weight for non-dynamic steps)
  const topConcerns = concerns.slice(0, 5)
  for (let i = 0; i < topConcerns.length; i++) {
    const concern = topConcerns[i]
    const positionWeight = 1 / (i + 1) // First = 1, second = 0.5, etc.
    const combinedWeight = positionWeight * concern.weight

    totalWeight += combinedWeight

    const productScore = product.concernScores[concern.field]

    // Log warning if concernScores field is missing (wrong format)
    if (productScore === undefined) {
      console.warn(
        `[RecommendationEngine] Missing concernScore field "${concern.field}" for product "${product.name}" (id: ${product.id}). ` +
        `Available fields: ${Object.keys(product.concernScores).join(', ')}`
      )
    }

    weightedMatch += combinedWeight * ((productScore || 0) / 5)
  }

  // Part 2: Step's focus concerns (secondary - ~20% bonus for non-dynamic steps)
  const stepPurpose = STEP_PURPOSES[stepCategory]
  if (stepPurpose && stepPurpose.concernFocus !== 'DYNAMIC') {
    const stepConcerns = stepPurpose.concernFocus
    const stepWeight = 0.25 // 20% of total contribution

    for (const field of stepConcerns) {
      // Skip if already covered by user concerns
      if (topConcerns.some((c) => c.field === field)) continue

      const productScore = product.concernScores[field as keyof typeof product.concernScores]
      if (productScore !== undefined) {
        totalWeight += stepWeight
        weightedMatch += stepWeight * (productScore / 5)
      }
    }
  }

  return totalWeight > 0 ? weightedMatch / totalWeight : 0.5
}

/**
 * Calculate skin type compatibility (0-1)
 */
function calculateSkinTypeMatch(product: Product, userSkinType: SkinType): number {
  if (product.skinTypes.includes('all')) return 1.0
  if (product.skinTypes.includes(userSkinType)) return 1.0

  // Partial matches
  if (userSkinType === 'combination') {
    if (product.skinTypes.includes('normal')) return 0.8
    if (product.skinTypes.includes('oily') || product.skinTypes.includes('dry')) return 0.6
  }

  if (userSkinType === 'normal') return 0.7

  return 0.2
}

/**
 * Calculate ingredient preference match (0-1)
 */
function calculateIngredientMatch(product: Product, fullSurvey: FullSurveyData): number {
  if (fullSurvey.includeIngredients.length === 0) return 0.5

  let matchCount = 0
  for (const pref of fullSurvey.includeIngredients) {
    const ingredientName = SURVEY_INGREDIENT_TO_NAME[pref]
    if (!ingredientName) continue

    const ingredientNorm = normalizeIngredient(ingredientName)
    const hasIngredient = product.keyIngredients.some((ing) =>
      normalizeIngredient(ing).includes(ingredientNorm)
    )
    if (hasIngredient) matchCount++
  }

  return matchCount / fullSurvey.includeIngredients.length
}

/**
 * Calculate texture match for weather (0-1)
 */
function calculateTextureMatch(
  product: Product,
  weather: WeatherInsight,
  stepCategory: RoutineCategory,
  time: 'AM' | 'PM'
): number {
  // Get preferred textures for this step and time
  const preferredTextures = getPreferredTextures(stepCategory, time)

  if (preferredTextures.length === 0) return 0.8 // Step doesn't have texture preference

  // Check if product texture is preferred
  if (preferredTextures.includes(product.textureType)) {
    return 1.0
  }

  // Check weather-based texture feel
  const weatherTexture = mapWeatherToTexture(weather.textureRecommendation)
  if (product.textureFeel === weatherTexture) {
    return 0.9
  }

  // Adjacent textures
  if (product.textureFeel === 'medium') return 0.7

  return 0.4
}

function mapWeatherToTexture(rec: TextureRecommendation): TextureFeel {
  switch (rec) {
    case 'lightweight':
      return 'lightweight'
    case 'rich':
      return 'rich'
    default:
      return 'medium'
  }
}

/**
 * Calculate synergy bonus with already-selected products
 */
function calculateSynergyBonus(
  product: Product,
  selectedIngredients: string[]
): number {
  let maxBonus = 1.0

  for (const ingredient of product.keyIngredients) {
    const bonus = getSynergyBonus(ingredient, selectedIngredients)
    if (bonus > maxBonus) {
      maxBonus = bonus
    }
  }

  // Convert bonus multiplier to 0-1 score
  // 1.0 = no synergy, 1.5 = max synergy → 0.0 to 1.0
  return (maxBonus - 1.0) / 0.5
}

/**
 * Calculate time-appropriate bonus/penalty
 */
function calculateTimeScore(product: Product, time: 'AM' | 'PM'): number {
  let penalty = 0
  let bonus = 0

  for (const ingredient of product.keyIngredients) {
    const result = isTimeAppropriate(ingredient, time)
    if (!result.appropriate) {
      penalty += result.penalty
    } else if (result.penalty < 0) {
      bonus += Math.abs(result.penalty)
    }
  }

  // Return as bonus (positive = good) or penalty (negative = bad)
  return bonus - penalty
}

/**
 * Calculate unified avoid penalty (0 or 1)
 * Combines:
 * - User's avoid preferences (fragrance-free, alcohol-free, etc.)
 * - Pregnancy safety (if pregnant, avoid retinol, BHA, etc.)
 */
function calculateAvoidPenalty(product: Product, fullSurvey: FullSurveyData): number {
  // Check user's avoid preferences
  for (const avoid of fullSurvey.avoidIngredients) {
    switch (avoid) {
      case 'fragrance':
        if (!product.avoidFlags.fragranceFree) return 1
        break
      case 'alcohol':
        if (!product.avoidFlags.alcoholFree) return 1
        break
      case 'essential-oils':
        if (!product.avoidFlags.essentialOilFree) return 1
        break
      case 'parabens':
        if (!product.avoidFlags.parabenfree) return 1
        break
      case 'sulfates':
        if (!product.avoidFlags.sulfateFree) return 1
        break
      case 'silicones':
        if (!product.avoidFlags.siliconeFree) return 1
        break
      case 'mineral-oil':
        if (!product.avoidFlags.mineralOilFree) return 1
        break
    }
  }

  // Check pregnancy safety (integrated from pregnancyPenalty)
  if (fullSurvey.pregnancyStatus === 'yes') {
    // If product is marked pregnancy-safe, it's fine
    if (!product.avoidFlags.pregnancySafe) {
      // Check for pregnancy-unsafe ingredients
      for (const ingredient of product.keyIngredients) {
        const ingNorm = normalizeIngredient(ingredient)
        for (const unsafe of PREGNANCY_UNSAFE_INGREDIENTS) {
          if (ingNorm.includes(normalizeIngredient(unsafe))) {
            return 1
          }
        }
      }
    }
  }

  return 0
}

/**
 * Calculate conflict penalty with already-selected products (0 or 1)
 */
function calculateConflictPenalty(
  product: Product,
  selectedIngredients: string[]
): number {
  for (const productIng of product.keyIngredients) {
    for (const selectedIng of selectedIngredients) {
      if (hasConflict(productIng, selectedIng)) {
        return 1
      }
    }
  }
  return 0
}

// ============================================================================
// Phase 3: Main Scoring Function
// ============================================================================

/**
 * Score a product for a specific step
 *
 * Simplified formula (4 core terms + 4 optional for A/B testing):
 * Core:    concernMatch + skinTypeMatch - avoidPenalty - conflictPenalty
 * Optional: ingredientMatch + textureMatch + synergyBonus + timeBonus
 */
export function scoreProductForStep(
  product: Product,
  profile: UserProfile,
  stepCategory: RoutineCategory,
  time: 'AM' | 'PM',
  concerns: PrioritizedConcern[],
  selectedIngredients: string[]
): ProductScore {
  const skinType = mapSkinType(profile.survey.skinType || 'normal')

  // ============================================================================
  // Core terms (always active)
  // ============================================================================
  const concernMatch = calculateConcernMatch(product, concerns, stepCategory) // Now unified with stepPurpose
  const skinTypeMatch = calculateSkinTypeMatch(product, skinType)
  const avoidPenalty = calculateAvoidPenalty(product, profile.fullSurvey) // Now unified with pregnancy
  const conflictPenalty = calculateConflictPenalty(product, selectedIngredients)

  // ============================================================================
  // Optional terms for A/B testing (set WEIGHTS to 0 to disable)
  // ============================================================================
  const ingredientMatch = calculateIngredientMatch(product, profile.fullSurvey)
  const textureMatch = calculateTextureMatch(product, profile.weather, stepCategory, time)
  const synergyBonus = calculateSynergyBonus(product, selectedIngredients)
  const timeScore = calculateTimeScore(product, time)

  // Get average severity weight for concern scoring
  const avgSeverityWeight =
    concerns.length > 0
      ? concerns.slice(0, 3).reduce((sum, c) => sum + c.weight, 0) / Math.min(3, concerns.length)
      : 1.0

  // Calculate total score (simplified formula)
  const totalScore =
    // Core terms
    concernMatch * WEIGHTS.concernMatch * avgSeverityWeight +
    skinTypeMatch * WEIGHTS.skinTypeMatch -
    avoidPenalty * WEIGHTS.avoidPenalty -
    conflictPenalty * WEIGHTS.conflictPenalty
    // // Optional terms (can be disabled by setting weight to 0)
    // ingredientMatch * WEIGHTS.ingredientMatch +
    // textureMatch * WEIGHTS.textureMatch +
    // synergyBonus * WEIGHTS.synergyBonus +
    // timeScore * WEIGHTS.timeBonus

  // Generate match reasons
  const matchReasons = generateMatchReasons(product, concerns, profile, time)

  return {
    product,
    totalScore,
    breakdown: {
      concernMatch,
      skinTypeMatch,
      avoidPenalty,
      conflictPenalty,
      ingredientMatch,
      textureMatch,
      synergyBonus,
      timeBonus: timeScore,
    },
    matchReasons,
  }
}

function mapSkinType(surveyType: string): SkinType {
  switch (surveyType) {
    case 'dry':
      return 'dry'
    case 'oily':
      return 'oily'
    case 'combination':
      return 'combination'
    default:
      return 'normal'
  }
}

/**
 * Generate human-readable match reasons
 */
function generateMatchReasons(
  product: Product,
  concerns: PrioritizedConcern[],
  profile: UserProfile,
  time: 'AM' | 'PM'
): string[] {
  const reasons: string[] = []

  // Top concern match
  const topConcern = concerns[0]
  if (topConcern && product.concernScores[topConcern.field] >= 4) {
    const displayName = topConcern.field.replace(/_/g, ' ').toLowerCase()
    reasons.push(`Excellent for ${displayName} (your #1 concern)`)
  }

  // Severity-specific
  const severeConcerns = concerns.filter((c) => c.severity === 'severe')
  if (severeConcerns.length > 0) {
    const addressed = severeConcerns.filter(
      (c) => product.concernScores[c.field] >= 3
    )
    if (addressed.length > 0) {
      reasons.push(`Targets ${addressed.length} of your severe concerns`)
    }
  }

  // Key ingredients match
  const matchedIngredients = profile.fullSurvey.includeIngredients
    .filter((pref) => {
      const name = SURVEY_INGREDIENT_TO_NAME[pref]
      const nameNorm = normalizeIngredient(name)
      return product.keyIngredients.some((ing) =>
        normalizeIngredient(ing).includes(nameNorm)
      )
    })
    .slice(0, 2)
    .map((pref) => SURVEY_INGREDIENT_TO_NAME[pref])

  if (matchedIngredients.length > 0) {
    reasons.push(`Contains ${matchedIngredients.join(', ')}`)
  }

  // Time-appropriate
  const hasRetinol = product.keyIngredients.some((i) =>
    normalizeIngredient(i).includes('retinol')
  )
  const hasVitC = product.keyIngredients.some((i) =>
    normalizeIngredient(i).includes('vitaminc')
  )

  if (time === 'PM' && hasRetinol) {
    reasons.push('Retinol optimized for evening use')
  }
  if (time === 'AM' && hasVitC) {
    reasons.push('Vitamin C provides daytime antioxidant protection')
  }

  // Clean beauty
  if (product.avoidFlags.fragranceFree && profile.fullSurvey.avoidIngredients.includes('fragrance')) {
    reasons.push('Fragrance-free formula')
  }

  return reasons.slice(0, 3)
}

// ============================================================================
// Phase 4: Routine Builder
// ============================================================================

// Step definitions based on DB AM_Step and PM_Step fields
// Step numbers come directly from the spreadsheet
interface RoutineStepDef {
  step: number // The step number (matches DB amStep/pmStep values)
  label: string
  category: RoutineCategory // For UI labels and concern focus lookup
  optional?: boolean // Optional steps (2.5 and 7.5 in PM)
}

const AM_STEP_DEFS: RoutineStepDef[] = [
  { step: 1, label: 'Cleanser', category: 'cleanser' },
  { step: 2, label: 'Toner', category: 'toner' },
  { step: 3, label: 'Serum', category: 'serum' },
  { step: 4, label: 'Eye Cream', category: 'eye-cream' },
  { step: 5, label: 'Moisturizer', category: 'moisturizer' },
  { step: 6, label: 'Sunscreen', category: 'sunscreen' },
]

const PM_STEP_DEFS: RoutineStepDef[] = [
  { step: 1, label: '1st Cleanser', category: 'cleanser' },
  { step: 2, label: '2nd Cleanser', category: 'cleanser' },
  { step: 3, label: 'Additional Care', category: 'toner' },
  { step: 4, label: 'Sheet Mask', category: 'sheet-mask' },
  { step: 5, label: 'Serum', category: 'serum' },
  { step: 6, label: 'Eye Cream', category: 'eye-cream' },
  { step: 7, label: 'Moisturizer', category: 'moisturizer' },
  { step: 8, label: 'Treatment', category: 'sleeping-mask' },
]

/**
 * Find products for a specific step number using DB step fields
 * This replaces the old category-based filtering
 */
function findProductsForStep(
  products: Product[],
  stepNumber: number,
  time: 'AM' | 'PM'
): Product[] {
  return products.filter((p) => {
    // Filter out products with no price
    if (!p.price || p.price <= 0) return false

    // Use the step field from DB instead of category
    const productStep = time === 'AM' ? p.amStep : p.pmStep
    return productStep === stepNumber
  })
}

/**
 * Check if user's skin type matches the optionalRec field
 * optionalRec contains comma-separated skin conditions like "Dry, Dehydrated-Oily"
 */
function matchesOptionalRec(optionalRec: string | undefined, skinType: SkinType): boolean {
  if (!optionalRec) return true // No restriction = matches all

  const conditions = optionalRec.toLowerCase().split(',').map(s => s.trim())

  // Map skin type to possible matches
  const skinTypeMatches: Record<SkinType, string[]> = {
    dry: ['dry', 'dehydrated'],
    oily: ['oily', 'dehydrated-oily'],
    combination: ['combination', 'combo', 'dehydrated-oily'],
    normal: ['normal', 'all'],
    all: ['all'],
  }

  const matchTerms = skinTypeMatches[skinType] || []
  return conditions.some(cond =>
    matchTerms.some(term => cond.includes(term))
  )
}

/**
 * Find products for a step by tier (cheapest/middle/expensive from qualified products)
 * Uses step number from DB (amStep/pmStep) instead of category filtering.
 * 1. Score ALL products for the step
 * 2. Filter to qualified products (score >= threshold, no conflicts)
 * 3. Pick cheapest, middle-priced, and most expensive from qualified set
 */
function findProductsByTier(
  products: Product[],
  stepDef: RoutineStepDef,
  time: 'AM' | 'PM',
  profile: UserProfile,
  concerns: PrioritizedConcern[],
  selectedIngredients: string[],
  selectedProductIds: Set<string> = new Set()
): { affordable: ProductScore | null; mainstream: ProductScore | null; premium: ProductScore | null } {
  const skinType = mapSkinType(profile.survey.skinType || 'normal')

  // Get all products for this step number, excluding already-selected products
  let stepProducts = findProductsForStep(products, stepDef.step, time)
    .filter(p => !selectedProductIds.has(p.id))

  // For optional steps with optionalRec, filter by skin type match
  if (stepDef.optional) {
    stepProducts = stepProducts.filter(p =>
      matchesOptionalRec(p.optionalRec, skinType)
    )
  }

  if (stepProducts.length === 0) {
    return { affordable: null, mainstream: null, premium: null }
  }

  // Score all products using the step's category for concern matching
  const scored = stepProducts.map((p) =>
    scoreProductForStep(p, profile, stepDef.category, time, concerns, selectedIngredients)
  )

  // Filter to qualified products (decent score, no conflicts)
  const qualified = scored.filter(
    (s) => s.totalScore >= MIN_QUALIFIED_SCORE && s.breakdown.conflictPenalty === 0
  )

  // If no qualified products, fall back to all scored (sorted by score)
  const candidates = qualified.length > 0 ? qualified : scored.filter((s) => s.totalScore > 0)

  if (candidates.length === 0) {
    return { affordable: null, mainstream: null, premium: null }
  }

  // Sort by price (low to high)
  const sortedByPrice = [...candidates].sort((a, b) => a.product.price - b.product.price)

  // Pick cheapest, middle, and most expensive
  const affordable = sortedByPrice[0]
  const premium = sortedByPrice[sortedByPrice.length - 1]

  // Middle: if 3+ products, pick the median; otherwise use the higher-scored one
  let mainstream: ProductScore
  if (sortedByPrice.length >= 3) {
    const midIndex = Math.floor(sortedByPrice.length / 2)
    mainstream = sortedByPrice[midIndex]
  } else if (sortedByPrice.length === 2) {
    // With 2 products, mainstream = the one with better score
    mainstream = sortedByPrice[0].totalScore >= sortedByPrice[1].totalScore
      ? sortedByPrice[0]
      : sortedByPrice[1]
  } else {
    // Only 1 product - use it for all tiers
    mainstream = sortedByPrice[0]
  }

  return {
    affordable: affordable || null,
    mainstream: mainstream || null,
    premium: premium || null,
  }
}

/**
 * Build complete AM/PM routine using step-based filtering (v2)
 * Products are now filtered by amStep/pmStep fields from DB instead of routineCategory
 */
export function buildRoutine(products: Product[], profile: UserProfile): RecommendedRoutine {
  // Get prioritized concerns
  const baseConcerns = prioritizeConcerns(profile.analysisScores)
  const allConcerns = addSurveyConcerns(baseConcerns, profile.fullSurvey)

  // Track selected ingredients for conflict checking
  let amSelectedIngredients: string[] = []
  let pmSelectedIngredients: string[] = []

  // Build steps for a time slot using step definitions
  const buildStepsForTime = (
    stepDefs: RoutineStepDef[],
    time: 'AM' | 'PM'
  ): RecommendedStep[] => {
    const selectedIngredients = time === 'AM' ? amSelectedIngredients : pmSelectedIngredients
    // Track product IDs to prevent same product in multiple steps (e.g., double cleanse)
    const usedProductIds = new Set<string>()
    const steps: RecommendedStep[] = []
    let uiStepNumber = 1

    // Check if user is sensitive (for skipping certain optional steps)
    const isSensitive = profile.fullSurvey.currentConditions.includes('sensitivity')
    const concernScores: Record<string, number> = {}
    for (const c of allConcerns) {
      concernScores[c.field] = c.score
    }

    for (const stepDef of stepDefs) {
      // Skip optional steps based on user conditions
      if (stepDef.optional) {
        const stepPurpose = STEP_PURPOSES[stepDef.category]
        if (stepPurpose && !shouldIncludeStep(stepDef.category, concernScores, isSensitive)) {
          continue
        }
      }

      // Find products for all tiers using step-based filtering
      const tierResults = findProductsByTier(
        products,
        stepDef,
        time,
        profile,
        allConcerns,
        selectedIngredients,
        usedProductIds
      )

      const productsForStep: RecommendedStep['products'] = {
        affordable: tierResults.affordable?.product || null,
        mainstream: tierResults.mainstream?.product || null,
        premium: tierResults.premium?.product || null,
      }

      // Skip if no products found for this step
      if (!productsForStep.affordable && !productsForStep.mainstream && !productsForStep.premium) {
        continue
      }

      // Determine selected tier
      let selectedTier: PriceTier = 'mainstream'
      if (!productsForStep.mainstream) {
        if (productsForStep.affordable) selectedTier = 'affordable'
        else if (productsForStep.premium) selectedTier = 'premium'
      }

      // Get match reasons from the best available tier result
      const matchReasons: string[] =
        tierResults.mainstream?.matchReasons ||
        tierResults.affordable?.matchReasons ||
        tierResults.premium?.matchReasons ||
        []

      // Add selected product's ingredients to tracking
      const selectedProduct = productsForStep[selectedTier]
      if (selectedProduct) {
        selectedIngredients.push(...selectedProduct.keyIngredients)
      }

      // Track all product IDs to prevent duplicates across steps
      if (productsForStep.affordable) usedProductIds.add(productsForStep.affordable.id)
      if (productsForStep.mainstream) usedProductIds.add(productsForStep.mainstream.id)
      if (productsForStep.premium) usedProductIds.add(productsForStep.premium.id)

      // Get step purpose for description
      const stepPurpose = STEP_PURPOSES[stepDef.category]

      steps.push({
        step: uiStepNumber,
        category: stepDef.category,
        label: stepDef.label,
        description: stepPurpose?.purpose || '',
        required: !stepDef.optional,
        products: productsForStep,
        selectedTier,
        matchReasons,
      })

      uiStepNumber++
    }

    // Update global selected ingredients
    if (time === 'AM') {
      amSelectedIngredients = selectedIngredients
    } else {
      pmSelectedIngredients = selectedIngredients
    }

    return steps
  }

  const amRoutine = buildStepsForTime(AM_STEP_DEFS, 'AM')
  const pmRoutine = buildStepsForTime(PM_STEP_DEFS, 'PM')

  // Calculate totals
  const calculateTotal = (tier: PriceTier): number => {
    let total = 0
    const seen = new Set<string>()

    for (const step of [...amRoutine, ...pmRoutine]) {
      const product = step.products[tier]
      if (product && !seen.has(product.id)) {
        total += product.price
        seen.add(product.id)
      }
    }
    return total
  }

  const calculateCurrentTotal = (): number => {
    let total = 0
    const seen = new Set<string>()

    for (const step of [...amRoutine, ...pmRoutine]) {
      const product = step.products[step.selectedTier]
      if (product && !seen.has(product.id)) {
        total += product.price
        seen.add(product.id)
      }
    }
    return total
  }

  return {
    am: amRoutine,
    pm: pmRoutine,
    totalPrice: {
      affordable: calculateTotal('affordable'),
      mainstream: calculateTotal('mainstream'),
      premium: calculateTotal('premium'),
      current: calculateCurrentTotal(),
    },
  }
}

// ============================================================================
// Phase 5: Routine Validation
// ============================================================================

/**
 * Validate the built routine
 */
export function validateRoutine(routine: RecommendedRoutine, profile: UserProfile): RoutineValidation {
  const concerns = prioritizeConcerns(profile.analysisScores)
  const issues: string[] = []

  // Check 1: Top 3 concerns addressed
  const top3 = concerns.slice(0, 3)
  const allProducts = [...routine.am, ...routine.pm]
    .map((s) => s.products[s.selectedTier])
    .filter(Boolean) as Product[]

  let concernsCoveredCount = 0
  for (const concern of top3) {
    const addressed = allProducts.some(
      (p) => p.concernScores[concern.field] >= 3
    )
    if (addressed) concernsCoveredCount++
  }

  const concernsCovered = concernsCoveredCount >= Math.min(2, top3.length)
  if (!concernsCovered) {
    issues.push(`Only ${concernsCoveredCount}/${top3.length} top concerns addressed`)
  }

  // Check 2: No conflicts within same time slot
  const amIngredients = routine.am
    .map((s) => s.products[s.selectedTier])
    .filter(Boolean)
    .flatMap((p) => p!.keyIngredients)

  const pmIngredients = routine.pm
    .map((s) => s.products[s.selectedTier])
    .filter(Boolean)
    .flatMap((p) => p!.keyIngredients)

  const amHasConflict = hasAnyConflict(amIngredients)
  const pmHasConflict = hasAnyConflict(pmIngredients)
  const noConflicts = !amHasConflict && !pmHasConflict

  if (amHasConflict) issues.push('AM routine has ingredient conflicts')
  if (pmHasConflict) issues.push('PM routine has ingredient conflicts')

  // Check 3: Active balance
  const amActives = countActiveClasses(amIngredients)
  const pmActives = countActiveClasses(pmIngredients)
  const activeBalance = amActives.size <= 2 && pmActives.size <= 3

  if (amActives.size > 2) issues.push(`AM has ${amActives.size} active classes (max 2)`)
  if (pmActives.size > 3) issues.push(`PM has ${pmActives.size} active classes (max 3)`)

  // Check 4: Sunscreen in AM
  const hasSunscreen = routine.am.some(
    (s) => s.category === 'sunscreen' && s.products[s.selectedTier]
  )

  if (!hasSunscreen) issues.push('AM routine missing sunscreen')

  return {
    concernsCovered,
    noConflicts,
    activeBalance,
    hasSunscreen,
    issues,
  }
}

// ============================================================================
// Utility: Get top concerns for display
// ============================================================================

export function getTopConcernsForDisplay(profile: UserProfile): {
  concern: string
  severity: 'mild' | 'moderate' | 'severe'
  score: number
}[] {
  const concerns = prioritizeConcerns(profile.analysisScores)

  return concerns.slice(0, 5).map((c) => ({
    concern: c.field.replace(/_/g, ' '),
    severity: c.severity,
    score: c.score,
  }))
}

// Legacy export for backwards compatibility
export function scoreProduct(product: Product, profile: UserProfile): ProductScore {
  const concerns = prioritizeConcerns(profile.analysisScores)
  return scoreProductForStep(product, profile, product.routineCategory, 'both' as 'AM', concerns, [])
}
